import { useMemo, useState } from "react";
import { ScatterChart, Scatter, XAxis, YAxis, ZAxis, Tooltip, ReferenceLine, ResponsiveContainer, LineChart, Line, Cell } from "recharts";
import { Search, ArrowUpDown } from "lucide-react";
import { computeGroupRows, computeAgentRows, trendSeries, sortRows, median, fmtN, fmtPct, fmtInt, kpisOf, IV_METRICS, C, SITE_COLORS } from "./ivUtils";

const card = "mk-card";
const th = "font-mono-hud cursor-pointer select-none whitespace-nowrap px-3 py-2.5 text-[9px] uppercase tracking-[0.12em] text-thdim hover:text-thcyan";
const SortTh = ({ label, k, sort, setSort, right = true }) => (
  <th className={`${th} ${right ? "text-right" : "text-left"}`} onClick={() => setSort((s) => ({ k, dir: s.k === k ? -s.dir : 1 }))}>
    <span className="inline-flex items-center gap-1">{label}{sort.k === k && <ArrowUpDown className="h-2.5 w-2.5 text-thcyan" />}</span>
  </th>
);
const MD_METRICS = [{ k: "mdVoc", label: "VOC (Monthly_Dump)", pct: true }, { k: "mdQ", label: "Quality (Monthly_Dump)", pct: true }, { k: "mdAdh", label: "Adherence (Monthly_Dump)", pct: true }];

export default function IvTlAgentsView({ data, slices, site, onOpenAgent }) {
  const [gSort, setGSort] = useState({ k: "voc", dir: 1 });
  const [aSort, setASort] = useState({ k: "composite", dir: 1 });
  const [selGrp, setSelGrp] = useState("");
  const [search, setSearch] = useState("");
  const [mode, setMode] = useState("w");
  const [level, setLevel] = useState("overall");
  const [entity, setEntity] = useState("");

  const groups = useMemo(() => sortRows(computeGroupRows(data, slices), gSort.k, gSort.dir), [data, slices, gSort]);
  const agentsAll = useMemo(() => computeAgentRows(data, slices), [data, slices]);
  const agents = useMemo(() => {
    let rows = agentsAll;
    if (selGrp) rows = rows.filter((r) => r.grp === selGrp);
    if (search) { const q = search.toLowerCase(); rows = rows.filter((r) => r.name.toLowerCase().includes(q) || r.c.includes(q) || r.grp.toLowerCase().includes(q)); }
    return sortRows(rows, aSort.k, aSort.dir);
  }, [agentsAll, selGrp, search, aSort]);

  const teamRow = useMemo(() => {
    const set = selGrp ? new Set(agentsAll.filter((r) => r.grp === selGrp).map((r) => r.c)) : null;
    const scoped = {};
    Object.entries(slices).forEach(([f, rows]) => { scoped[f] = set ? rows.filter((r) => set.has(r.c)) : rows; });
    return kpisOf(scoped);
  }, [slices, selGrp, agentsAll]);

  const trend = useMemo(() => {
    let ciscos = null;
    if (level === "grp" && entity) ciscos = new Set(Object.keys(data.agents).filter((c) => (data.agents[c].tl || data.agents[c].team) === entity));
    if (level === "agent" && entity) ciscos = new Set([entity]);
    return trendSeries(data, { mode, ciscos, site: level === "overall" ? site : "All" });
  }, [data, mode, level, entity, site]);

  const grpNames = useMemo(() => [...new Set(Object.values(data.agents).map((a) => a.tl || a.team).filter((g) => g && g !== "-"))].sort(), [data]);
  const agentOptions = useMemo(() => Object.entries(data.agents).filter(([, a]) => a.name).sort((a, b) => a[1].name.localeCompare(b[1].name)), [data]);

  const quadrant = useMemo(() => agentsAll.filter((r) => r.surveys >= 3 && r.aht != null), [agentsAll]);
  const medAht = median(quadrant.map((r) => r.aht));
  const medVoc = median(quadrant.map((r) => r.voc).filter((v) => v != null));
  const metrics = IV_METRICS(data.targets);

  return (
    <div className="space-y-8">
      <div className={card} data-testid="iv-tl-table">
        <p className="font-mono-hud px-5 pt-4 text-[10px] uppercase tracking-[0.25em] text-thdim">Team / TL League · click a row to open the team</p>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-left">
            <thead><tr className="border-b border-thborder/10">
              <SortTh label="Team / TL" k="grp" sort={gSort} setSort={setGSort} right={false} />
              <SortTh label="Agents" k="agents" sort={gSort} setSort={setGSort} />
              <SortTh label="Surveys" k="surveys" sort={gSort} setSort={setGSort} />
              <SortTh label="VOC %" k="voc" sort={gSort} setSort={setGSort} />
              <SortTh label="Answered" k="answered" sort={gSort} setSort={setGSort} />
              <SortTh label="Held %" k="heldPct" sort={gSort} setSort={setGSort} />
              <SortTh label="AHT" k="aht" sort={gSort} setSort={setGSort} />
              <SortTh label="PMT %" k="pmt" sort={gSort} setSort={setGSort} />
              <SortTh label="QSL %" k="qsl" sort={gSort} setSort={setGSort} />
            </tr></thead>
            <tbody>
              {groups.map((r) => (
                <tr key={r.grp} data-testid={`iv-tl-row-${r.grp.replace(/\s+/g, "-")}`} onClick={() => setSelGrp(r.grp === selGrp ? "" : r.grp)}
                  className={`cursor-pointer border-b border-thborder/5 hover:bg-thhover/5 ${selGrp === r.grp ? "bg-thcyan/5" : ""}`}>
                  <td className="px-3 py-2.5 text-xs font-medium text-thstrong">{r.grp}</td>
                  <td className="px-3 py-2.5 text-right font-mono-hud text-xs text-thbody">{r.agents}</td>
                  <td className="px-3 py-2.5 text-right font-mono-hud text-xs text-thbody">{fmtInt(r.surveys) || "—"}</td>
                  <td className="px-3 py-2.5 text-right font-mono-hud text-xs font-semibold" style={{ color: r.voc == null ? undefined : r.voc >= data.targets.voc ? "#10B981" : "#F43F5E" }}>{fmtPct(r.voc)}</td>
                  <td className="px-3 py-2.5 text-right font-mono-hud text-xs text-thbody">{fmtInt(r.answered)}</td>
                  <td className="px-3 py-2.5 text-right font-mono-hud text-xs text-thbody">{fmtPct(r.heldPct)}</td>
                  <td className="px-3 py-2.5 text-right font-mono-hud text-xs text-thbody">{fmtN(r.aht, 0)}</td>
                  <td className="px-3 py-2.5 text-right font-mono-hud text-xs text-thbody">{fmtPct(r.pmt)}</td>
                  <td className="px-3 py-2.5 text-right font-mono-hud text-xs text-thbody">{fmtPct(r.qsl)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className={card} data-testid="iv-agent-table">
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 pt-4">
          <p className="font-mono-hud text-[10px] uppercase tracking-[0.25em] text-thdim">Agent Scorecards · click a row for the drawer</p>
          <div className="flex items-center gap-2">
            <select data-testid="iv-agent-grp-select" value={selGrp} onChange={(e) => setSelGrp(e.target.value)}
              className="rounded-md border border-thborder/10 bg-thinput px-2.5 py-1.5 text-xs text-thstrong focus:border-thcyan focus:outline-none">
              <option value="">All teams</option>
              {grpNames.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
            <div className="relative">
              <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-thdim" />
              <input data-testid="iv-agent-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search agent, ID, team..."
                className="rounded-md border border-thborder/10 bg-thinput py-1.5 pl-8 pr-3 text-xs text-thstrong focus:border-thcyan focus:outline-none" />
            </div>
          </div>
        </div>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-left">
            <thead><tr className="border-b border-thborder/10">
              <SortTh label="Cisco" k="c" sort={aSort} setSort={setASort} right={false} />
              <SortTh label="Agent" k="name" sort={aSort} setSort={setASort} right={false} />
              <SortTh label="Site" k="site" sort={aSort} setSort={setASort} right={false} />
              <SortTh label="Team/TL" k="grp" sort={aSort} setSort={setASort} right={false} />
              <SortTh label="Srv" k="surveys" sort={aSort} setSort={setASort} />
              <SortTh label="VOC %" k="voc" sort={aSort} setSort={setASort} />
              <SortTh label="Ans" k="answered" sort={aSort} setSort={setASort} />
              <SortTh label="Held %" k="heldPct" sort={aSort} setSort={setASort} />
              <SortTh label="Hold" k="hold" sort={aSort} setSort={setASort} />
              <SortTh label="Talk" k="talk" sort={aSort} setSort={setASort} />
              <SortTh label="AHT" k="aht" sort={aSort} setSort={setASort} />
              <SortTh label="PMT" k="pmt" sort={aSort} setSort={setASort} />
              <SortTh label="Fatal" k="fatals" sort={aSort} setSort={setASort} />
              <SortTh label="QSL" k="qsl" sort={aSort} setSort={setASort} />
              <SortTh label="Adh" k="adh" sort={aSort} setSort={setASort} />
              <SortTh label="Comp" k="composite" sort={aSort} setSort={setASort} />
            </tr></thead>
            <tbody>
              <tr data-testid="iv-team-row" className="border-b border-thborder/10 bg-thcyan/5">
                <td className="px-3 py-2.5 font-mono-hud text-xs text-thcyan">—</td>
                <td className="px-3 py-2.5 text-xs font-semibold text-thcyan">{selGrp ? `TEAM — ${selGrp}` : "TEAM — all agents"}</td>
                <td className="px-3 py-2.5 text-xs text-thdim">—</td><td className="px-3 py-2.5 text-xs text-thdim">—</td>
                <td className="px-3 py-2.5 text-right font-mono-hud text-xs text-thcyan">{fmtInt(teamRow.surveys)}</td>
                <td className="px-3 py-2.5 text-right font-mono-hud text-xs font-semibold text-thcyan">{fmtPct(teamRow.voc)}</td>
                <td className="px-3 py-2.5 text-right font-mono-hud text-xs text-thcyan">{fmtInt(teamRow.answered)}</td>
                <td className="px-3 py-2.5 text-right font-mono-hud text-xs text-thcyan">{fmtPct(teamRow.heldPct)}</td>
                <td className="px-3 py-2.5 text-right font-mono-hud text-xs text-thcyan">{fmtN(teamRow.hold, 0)}</td>
                <td className="px-3 py-2.5 text-right font-mono-hud text-xs text-thcyan">{fmtN(teamRow.talk, 0)}</td>
                <td className="px-3 py-2.5 text-right font-mono-hud text-xs font-semibold text-thcyan">{fmtN(teamRow.aht, 0)}</td>
                <td className="px-3 py-2.5 text-right font-mono-hud text-xs text-thcyan">{fmtPct(teamRow.pmt)}</td>
                <td className="px-3 py-2.5 text-right font-mono-hud text-xs text-thdim">—</td>
                <td className="px-3 py-2.5 text-right font-mono-hud text-xs text-thcyan">{fmtPct(teamRow.qsl)}</td>
                <td className="px-3 py-2.5 text-right font-mono-hud text-xs text-thdim">—</td>
                <td className="px-3 py-2.5 text-right font-mono-hud text-xs text-thdim">—</td>
              </tr>
              {agents.map((r) => (
                <tr key={r.c} data-testid={`iv-agent-row-${r.c}`} onClick={() => onOpenAgent(r.c)} className="cursor-pointer border-b border-thborder/5 hover:bg-thhover/5">
                  <td className="px-3 py-2 font-mono-hud text-xs text-thdim">{r.c}</td>
                  <td className="max-w-[170px] truncate px-3 py-2 text-xs font-medium text-thstrong">{r.name}</td>
                  <td className="px-3 py-2 text-xs"><span style={{ color: SITE_COLORS[r.site] }}>{r.site}</span></td>
                  <td className="max-w-[120px] truncate px-3 py-2 text-xs text-thmute">{r.grp}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{r.surveys || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{fmtPct(r.voc)}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{fmtInt(r.answered) || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{fmtPct(r.heldPct)}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{fmtN(r.hold, 0)}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{fmtN(r.talk, 0)}</td>
                  <td className={`px-3 py-2 text-right font-mono-hud text-xs ${r.aht == null ? "text-thdim" : r.aht <= data.targets.aht ? "text-[#10B981]" : "text-[#F43F5E]"}`}>{fmtN(r.aht, 0)}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{fmtPct(r.pmt)}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{r.fatals ?? "—"}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{fmtPct(r.qsl)}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs text-thbody">{fmtPct(r.adh)}</td>
                  <td className="px-3 py-2 text-right font-mono-hud text-xs font-semibold text-thbody">{fmtPct(r.composite)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="px-5 py-3 text-xs text-thdim">{agents.length} agents · blanks sort last · composite = simple average of available VOC / PMT / QSL / Adherence</p>
      </div>

      <div className={`${card} p-5`} data-testid="iv-trend-explorer">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <p className="font-mono-hud text-[10px] uppercase tracking-[0.25em] text-thdim">Trend Explorer · full history (date filter ignored) · monthly adds Monthly_Dump metrics</p>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex gap-1 rounded-lg bg-thhover/5 p-1">
              {[["w", "Weekly"], ["m", "Monthly"]].map(([m, l]) => (
                <button key={m} data-testid={`iv-trend-mode-${l.toLowerCase()}`} onClick={() => setMode(m)}
                  className={`rounded-md px-3 py-1 text-xs font-medium ${mode === m ? "bg-thcyan/15 text-thcyan" : "text-thmute hover:text-thbody"}`}>{l}</button>
              ))}
            </div>
            <select data-testid="iv-trend-level" value={level} onChange={(e) => { setLevel(e.target.value); setEntity(""); }}
              className="rounded-md border border-thborder/10 bg-thinput px-2.5 py-1.5 text-xs text-thstrong focus:border-thcyan focus:outline-none">
              <option value="overall">Overall</option><option value="grp">By Team/TL</option><option value="agent">By Agent</option>
            </select>
            {level === "grp" && (
              <select data-testid="iv-trend-entity" value={entity} onChange={(e) => setEntity(e.target.value)}
                className="rounded-md border border-thborder/10 bg-thinput px-2.5 py-1.5 text-xs text-thstrong focus:border-thcyan focus:outline-none">
                <option value="">Choose team…</option>
                {grpNames.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            )}
            {level === "agent" && (
              <select data-testid="iv-trend-entity" value={entity} onChange={(e) => setEntity(e.target.value)}
                className="max-w-[220px] rounded-md border border-thborder/10 bg-thinput px-2.5 py-1.5 text-xs text-thstrong focus:border-thcyan focus:outline-none">
                <option value="">Choose agent…</option>
                {agentOptions.map(([c, a]) => <option key={c} value={c}>{a.name} ({c})</option>)}
              </select>
            )}
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {[...metrics, ...(mode === "m" ? MD_METRICS : [])].map((m) => {
            const pts = trend.map((p) => ({ label: p.label, v: p[m.k] })).filter((p) => p.v != null);
            if (pts.length < 2) return null;
            return (
              <div key={m.k} className="rounded-lg border border-thborder/10 bg-thhover/5 p-3">
                <p className="mb-1 text-xs font-medium text-thbody">{m.label}</p>
                <div className="h-28">
                  <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={100} initialDimension={{ width: 1, height: 1 }}>
                    <LineChart data={pts} margin={{ top: 4, right: 8, bottom: 0, left: -14 }}>
                      <XAxis dataKey="label" tick={{ fill: "#7a8fb5", fontSize: 9, fontFamily: "JetBrains Mono" }} interval="preserveStartEnd" />
                      <YAxis tick={{ fill: "#7a8fb5", fontSize: 9, fontFamily: "JetBrains Mono" }} domain={["auto", "auto"]} />
                      <Tooltip contentStyle={{ background: "rgb(var(--th-solid))", border: "1px solid rgb(var(--th-border) / 0.14)", borderRadius: 10, fontSize: 11, color: "rgb(var(--th-strong))" }} labelStyle={{ color: "rgb(var(--th-mute))" }} />
                      {m.target != null && <ReferenceLine y={m.target} stroke={C.yellow} strokeDasharray="5 4" strokeOpacity={0.7} />}
                      <Line type="monotone" dataKey="v" stroke={m.lower ? C.purple : C.green} strokeWidth={2} dot={{ r: 2 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className={`${card} p-5`} data-testid="iv-quadrant">
        <p className="font-mono-hud text-[10px] uppercase tracking-[0.25em] text-thdim">AHT vs VOC Quadrant · agents with ≥3 surveys · click a point for the drawer</p>
        <p className="mt-1 mb-2 text-xs text-thdim">Crosshairs at the median. Ideal = low AHT, high VOC (top-left).</p>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={100} initialDimension={{ width: 1, height: 1 }}>
            <ScatterChart margin={{ top: 10, right: 20, bottom: 10, left: -8 }}>
              <XAxis dataKey="aht" name="AHT (s)" type="number" tick={{ fill: "#7a8fb5", fontSize: 10, fontFamily: "JetBrains Mono" }} domain={["auto", "auto"]}
                label={{ value: "AHT (sec)", position: "insideBottom", offset: -6, fill: "#64748b", fontSize: 10 }} />
              <YAxis dataKey="voc" name="VOC %" type="number" tick={{ fill: "#7a8fb5", fontSize: 10, fontFamily: "JetBrains Mono" }} domain={["auto", "auto"]} />
              <ZAxis dataKey="surveys" range={[35, 220]} name="Surveys" />
              {medAht != null && <ReferenceLine x={medAht} stroke="#7a8fb5" strokeDasharray="4 4" />}
              {medVoc != null && <ReferenceLine y={medVoc} stroke="#7a8fb5" strokeDasharray="4 4" />}
              <Tooltip cursor={{ strokeDasharray: "3 3" }} content={({ payload }) => {
                const p = payload?.[0]?.payload;
                if (!p) return null;
                return (
                  <div className="rounded-xl border border-thborder/14 bg-thsolid px-3 py-2 font-mono-hud text-xs text-thstrong shadow-card">
                    <p className="font-semibold">{p.name} <span className="font-mono-hud text-[10px] text-slate-400">{p.c}</span></p>
                    <p className="text-slate-300">VOC {fmtPct(p.voc)} · AHT {fmtN(p.aht, 0)}s · {p.surveys} surveys · {p.site}</p>
                  </div>
                );
              }} />
              <Scatter data={quadrant} onClick={(p) => p && onOpenAgent(p.c)}>
                {quadrant.map((r) => <Cell key={r.c} fill={SITE_COLORS[r.site]} fillOpacity={0.85} className="cursor-pointer" />)}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-2 flex gap-4">
          {Object.entries(SITE_COLORS).map(([s, c]) => (
            <span key={s} className="flex items-center gap-1.5 text-xs text-thmute"><span className="h-2.5 w-2.5 rounded-full" style={{ background: c }} />{s}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
